import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription, take } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { BlogLang, BlogsService } from '../../../../services/blog';

type BlogCategoryId = 'all' | 'career' | 'cv' | 'travel' | 'interview';

interface BlogCategory {
  id: BlogCategoryId;
  labelKey: string;
}

interface StaticBlogPost {
  id: number;
  slug: string;
  iconImg: string;
  category: Exclude<BlogCategoryId, 'all'>;
  categoryKey: string;
  titleKey: string;
  excerptKey: string;
  dateKey: string;
  readTimeKey: string;
  tagsKeys: string[];
  featured?: boolean;
}

interface BlogPost {
  id: number | string;
  slug: string;
  iconImg: string;
  category: Exclude<BlogCategoryId, 'all'>;
  categoryLabel: string;
  title: string;
  excerpt: string;
  dateLabel: string;
  readTimeLabel: string;
  tags: string[];
  featured?: boolean;
  isApi?: boolean;
}

@Component({
  selector: 'app-blogsfullpage',
  templateUrl: './blogsfullpage.component.html',
  styleUrl: './blogsfullpage.component.scss',
})
export class BlogsfullpageComponent implements OnInit, OnDestroy {
  searchTerm = '';
  activeCategory: BlogCategoryId = 'all';

  blogs: BlogPost[] = [];

  loadingBlogs = false;
  blogsError = '';

  isAr = false;
  currentLang: BlogLang = 'en';

  categories: BlogCategory[] = [
    { id: 'all', labelKey: 'atw.blogs.categories.all' },
    { id: 'career', labelKey: 'atw.blogs.categories.career' },
    { id: 'cv', labelKey: 'atw.blogs.categories.cv' },
    { id: 'travel', labelKey: 'atw.blogs.categories.travel' },
    { id: 'interview', labelKey: 'atw.blogs.categories.interview' },
  ];

  private isBrowser: boolean;
  private langSub?: Subscription;

  private staticBlogs: StaticBlogPost[] = [
    {
      id: 1,
      slug: 'how-to-build-a-strong-cv',
      iconImg: 'assets/as/30.webp',
      category: 'cv',
      categoryKey: 'atw.blogs.posts.post1.category',
      titleKey: 'atw.blogs.posts.post1.title',
      excerptKey: 'atw.blogs.posts.post1.excerpt',
      dateKey: 'atw.blogs.posts.post1.date',
      readTimeKey: 'atw.blogs.posts.post1.readTime',
      tagsKeys: [
        'atw.blogs.tags.cv',
        'atw.blogs.tags.keywords',
        'atw.blogs.tags.jobs',
      ],
      featured: true,
    },
    {
      id: 2,
      slug: 'prepare-for-your-first-interview',
      iconImg: 'assets/as/17.webp',
      category: 'interview',
      categoryKey: 'atw.blogs.posts.post2.category',
      titleKey: 'atw.blogs.posts.post2.title',
      excerptKey: 'atw.blogs.posts.post2.excerpt',
      dateKey: 'atw.blogs.posts.post2.date',
      readTimeKey: 'atw.blogs.posts.post2.readTime',
      tagsKeys: [
        'atw.blogs.tags.interview',
        'atw.blogs.tags.confidence',
        'atw.blogs.tags.tips',
      ],
    },
    {
      id: 3,
      slug: 'top-career-mistakes-to-avoid',
      iconImg: 'assets/as/51.webp',
      category: 'career',
      categoryKey: 'atw.blogs.posts.post3.category',
      titleKey: 'atw.blogs.posts.post3.title',
      excerptKey: 'atw.blogs.posts.post3.excerpt',
      dateKey: 'atw.blogs.posts.post3.date',
      readTimeKey: 'atw.blogs.posts.post3.readTime',
      tagsKeys: [
        'atw.blogs.tags.career',
        'atw.blogs.tags.growth',
        'atw.blogs.tags.planning',
      ],
    },
    {
      id: 4,
      slug: 'working-abroad-checklist',
      iconImg: 'assets/as/50.webp',
      category: 'travel',
      categoryKey: 'atw.blogs.posts.post4.category',
      titleKey: 'atw.blogs.posts.post4.title',
      excerptKey: 'atw.blogs.posts.post4.excerpt',
      dateKey: 'atw.blogs.posts.post4.date',
      readTimeKey: 'atw.blogs.posts.post4.readTime',
      tagsKeys: [
        'atw.blogs.tags.travel',
        'atw.blogs.tags.documents',
        'atw.blogs.tags.visa',
      ],
    },
    {
      id: 5,
      slug: 'how-to-match-your-skills-with-jobs',
      iconImg: 'assets/as/52.webp',
      category: 'career',
      categoryKey: 'atw.blogs.posts.post5.category',
      titleKey: 'atw.blogs.posts.post5.title',
      excerptKey: 'atw.blogs.posts.post5.excerpt',
      dateKey: 'atw.blogs.posts.post5.date',
      readTimeKey: 'atw.blogs.posts.post5.readTime',
      tagsKeys: [
        'atw.blogs.tags.skills',
        'atw.blogs.tags.matching',
        'atw.blogs.tags.jobs',
      ],
    },
    {
      id: 6,
      slug: 'cv-keywords-that-help-you-get-noticed',
      iconImg: 'assets/as/2.webp',
      category: 'cv',
      categoryKey: 'atw.blogs.posts.post6.category',
      titleKey: 'atw.blogs.posts.post6.title',
      excerptKey: 'atw.blogs.posts.post6.excerpt',
      dateKey: 'atw.blogs.posts.post6.date',
      readTimeKey: 'atw.blogs.posts.post6.readTime',
      tagsKeys: [
        'atw.blogs.tags.cv',
        'atw.blogs.tags.keywords',
        'atw.blogs.tags.ats',
      ],
    },
  ];

  constructor(
    private blogsService: BlogsService,
    private transloco: TranslocoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.setCurrentLang();
    this.loadBlogs();

    this.langSub = this.transloco.langChanges$.subscribe((lang) => {
      const oldLang = this.currentLang;

      this.setCurrentLang(lang as BlogLang);

      if (oldLang !== this.currentLang) {
        this.loadBlogs();
      }
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  loadBlogs(): void {
    this.loadingBlogs = true;
    this.blogsError = '';

    this.transloco
      .load(this.currentLang)
      .pipe(take(1))
      .subscribe({
        next: () => {
          const mappedStaticBlogs = this.getMappedStaticBlogs();

          this.blogsService
            .getBlogs({
              lang: this.currentLang,
              status: 'published',
            })
            .subscribe({
              next: (res) => {
                const apiBlogs = this.extractBlogs(res);

                const mappedApiBlogs = apiBlogs.map((blog: any, index: number) =>
                  this.mapApiBlog(blog, index)
                );

                this.blogs = [...mappedStaticBlogs, ...mappedApiBlogs];

                this.loadingBlogs = false;
              },
              error: (err) => {
                console.error('Blogs API Error:', err);

                this.blogs = mappedStaticBlogs;
                this.blogsError = '';
                this.loadingBlogs = false;
              },
            });
        },
        error: (err) => {
          console.error('Transloco Load Error:', err);

          const mappedStaticBlogs = this.getMappedStaticBlogs();

          this.blogs = mappedStaticBlogs;
          this.loadingBlogs = false;
          this.blogsError = '';
        },
      });
  }

  get featuredBlog(): BlogPost | undefined {
    return this.blogs.find((blog) => blog.featured) || this.blogs[0];
  }

  get filteredBlogs(): BlogPost[] {
    const term = this.searchTerm.trim().toLowerCase();

    return this.blogs.filter((blog) => {
      const matchesCategory =
        this.activeCategory === 'all' || blog.category === this.activeCategory;

      const searchableText = [
        blog.slug,
        blog.category,
        blog.categoryLabel,
        blog.title,
        blog.excerpt,
        ...blog.tags,
      ]
        .join(' ')
        .toLowerCase();

      const matchesSearch = !term || searchableText.includes(term);

      return matchesCategory && matchesSearch;
    });
  }

  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
  }

  setCategory(category: BlogCategoryId): void {
    this.activeCategory = category;
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.activeCategory = 'all';
  }

  trackByBlog(index: number, blog: BlogPost): number | string {
    return blog.id || blog.slug || index;
  }

  trackByCategory(index: number, category: BlogCategory): string {
    return category.id;
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/as/30.webp';
  }

  private setCurrentLang(forceLang?: BlogLang): void {
    const savedLang = this.isBrowser
      ? localStorage.getItem('language')
      : this.transloco.getActiveLang();

    const lang = forceLang || savedLang || this.transloco.getActiveLang();

    this.isAr = lang === 'ar';
    this.currentLang = this.isAr ? 'ar' : 'en';

    if (this.transloco.getActiveLang() !== this.currentLang) {
      this.transloco.setActiveLang(this.currentLang);
    }
  }

  private getMappedStaticBlogs(): BlogPost[] {
    return this.staticBlogs.map((blog) => ({
      id: `static-${blog.id}`,
      slug: blog.slug,
      iconImg: blog.iconImg,
      category: blog.category,

      categoryLabel: this.t(blog.categoryKey),
      title: this.t(blog.titleKey),
      excerpt: this.t(blog.excerptKey),
      dateLabel: this.t(blog.dateKey),
      readTimeLabel: this.t(blog.readTimeKey),

      tags: blog.tagsKeys.map((tagKey) => this.t(tagKey)),

      featured: blog.featured,
      isApi: false,
    }));
  }

  private mapApiBlog(blog: any, index: number): BlogPost {
    const category = this.normalizeCategory(blog.category);

    return {
      id: `api-${blog.id || index}`,

      slug: blog.slug || blog.slug_en || blog.slug_ar || String(blog.id),

      iconImg: this.getBlogImage(blog),

      category,

      categoryLabel: this.getCategoryLabel(blog, category),

      title:
        this.getLocalizedField(blog, 'title') ||
        (this.isAr ? 'مقال بدون عنوان' : 'Untitled blog'),

      excerpt: this.getExcerpt(blog),

      dateLabel: this.formatDate(blog.published_at || blog.created_at),

      readTimeLabel: this.getLocalizedField(blog, 'read_time'),

      tags: this.getTags(blog),

      featured: this.toBoolean(blog.is_featured),

      isApi: true,
    };
  }

  private extractBlogs(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }

    if (Array.isArray(res?.data?.data)) {
      return res.data.data;
    }

    if (Array.isArray(res?.data)) {
      return res.data;
    }

    if (Array.isArray(res?.blogs?.data)) {
      return res.blogs.data;
    }

    if (Array.isArray(res?.blogs)) {
      return res.blogs;
    }

    return [];
  }

  private getLocalizedField(blog: any, field: string): string {
    if (!blog) {
      return '';
    }

    if (this.currentLang === 'ar') {
      return blog[`${field}_ar`] || blog[field] || blog[`${field}_en`] || '';
    }

    return blog[`${field}_en`] || blog[field] || blog[`${field}_ar`] || '';
  }

  private getExcerpt(blog: any): string {
    const excerpt = this.getLocalizedField(blog, 'excerpt');

    if (excerpt) {
      return this.stripHtml(excerpt);
    }

    const content = this.getLocalizedField(blog, 'content');

    return this.stripHtml(content).slice(0, 170);
  }

  private getCategoryLabel(
    blog: any,
    category: Exclude<BlogCategoryId, 'all'>
  ): string {
    const apiLabel =
      this.currentLang === 'ar'
        ? blog.category_ar || blog.category_en
        : blog.category_en || blog.category_ar;

    if (apiLabel) {
      return apiLabel;
    }

    const key = `atw.blogs.categories.${category}`;
    const translated = this.t(key);

    return translated && translated !== key ? translated : category;
  }

  private normalizeCategory(category: any): Exclude<BlogCategoryId, 'all'> {
    const value = String(category || '').trim().toLowerCase();

    if (value === 'cv') {
      return 'cv';
    }

    if (value === 'travel') {
      return 'travel';
    }

    if (value === 'interview') {
      return 'interview';
    }

    if (value === 'career') {
      return 'career';
    }

    return 'career';
  }

  private getTags(blog: any): string[] {
    const tags =
      this.currentLang === 'ar'
        ? blog.tags_ar || blog.tags_en
        : blog.tags_en || blog.tags_ar;

    if (!tags) {
      return [];
    }

    if (Array.isArray(tags)) {
      return tags.filter(Boolean);
    }

    if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);

        if (Array.isArray(parsed)) {
          return parsed.filter(Boolean);
        }
      } catch {
        return tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);
      }
    }

    return [];
  }

  private getBlogImage(blog: any): string {
    const image =
      blog.image_url ||
      blog.imageUrl ||
      blog.full_image_url ||
      blog.icon_url ||
      blog.iconUrl ||
      blog.image ||
      blog.icon;

    if (!image) {
      return 'assets/as/30.webp';
    }

    const imageValue = String(image);

    if (
      imageValue.startsWith('http://') ||
      imageValue.startsWith('https://') ||
      imageValue.startsWith('assets/') ||
      imageValue.startsWith('data:image')
    ) {
      return imageValue;
    }

    return `${environment.Server_URL}/${imageValue}`.replace(
      /([^:]\/)\/+/g,
      '$1'
    );
  }

  private formatDate(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(this.currentLang === 'ar' ? 'ar-EG' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  private stripHtml(value: string): string {
    if (!value) {
      return '';
    }

    return value.replace(/<[^>]*>/g, '').trim();
  }

  private toBoolean(value: any): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private t(key: string): string {
    return this.transloco.translate(key, {}, this.currentLang);
  }
}